import { AxiosObservable } from '@cbridge-utils/axios-observable';
import { ApiServiceType, BaseService } from '@cbridge-packages/api-base';
import {
  IApiCoverageServerCreate,
  IApiCoverageServerUpdate,
  IApiGetCoverageServersRes,
  IApiGetCoverageServerRes,
  IApiCreateCoverageServerRes,
} from '../_entities/IApiCoverageServer';
import {
  IApiCoverageReportCreate,
  IApiCoverageReportUpdate,
  IApiGetCoverageReportsRes,
  IApiCreateCoverageReportRes,
} from '../_entities/IApiCoverageReport';
import {
  IApiCoverageAccountCreate,
  IApiCoverageAccountUpdate,
  IApiCreateCoverageAccountRes,
} from '../_entities/IApiCoverageAccount';
import {
  IApiGetCoverageRulesRes,
  IApiUpdateCoverageRulesReq,
  IApiUpdateCoverageRulesRes,
} from '../_entities/IApiCoverageRule';

export class CoverageApiService extends BaseService {
  constructor() {
    super(ApiServiceType.ControlPlane);
  }

  // ── Reporting servers ─────────────────────────────────────────
  getCoverageServers(): AxiosObservable<IApiGetCoverageServersRes> {
    return this.api.get<IApiGetCoverageServersRes>('/external-reporting/servers');
  }

  getCoverageServer(serverId: number): AxiosObservable<IApiGetCoverageServerRes> {
    return this.api.get<IApiGetCoverageServerRes>(`/external-reporting/servers/${serverId}`);
  }

  createCoverageServer(payload: IApiCoverageServerCreate): AxiosObservable<IApiCreateCoverageServerRes> {
    return this.api.post<IApiCreateCoverageServerRes>('/external-reporting/servers', payload);
  }

  updateCoverageServer(serverId: number, payload: IApiCoverageServerUpdate): AxiosObservable<void> {
    return this.api.put<void>(`/external-reporting/servers/${serverId}`, payload);
  }

  deleteCoverageServer(serverId: number): AxiosObservable<void> {
    return this.api.delete<void>(`/external-reporting/servers/${serverId}`);
  }

  // ── Coverage reports (rule sets) ──────────────────────────────
  getCoverageReports(): AxiosObservable<IApiGetCoverageReportsRes> {
    return this.api.get<IApiGetCoverageReportsRes>('/external-reporting/reports');
  }

  createCoverageReport(serverId: number, payload: IApiCoverageReportCreate): AxiosObservable<IApiCreateCoverageReportRes> {
    return this.api.post<IApiCreateCoverageReportRes>(`/external-reporting/servers/${serverId}/reports`, payload);
  }

  updateCoverageReport(reportId: number, payload: IApiCoverageReportUpdate): AxiosObservable<void> {
    return this.api.put<void>(`/external-reporting/reports/${reportId}`, payload);
  }

  deleteCoverageReport(reportId: number): AxiosObservable<void> {
    return this.api.delete<void>(`/external-reporting/reports/${reportId}`);
  }

  // ── Coverage accounts ─────────────────────────────────────────
  createCoverageAccount(
    serverId: number,
    reportId: number,
    payload: IApiCoverageAccountCreate,
  ): AxiosObservable<IApiCreateCoverageAccountRes> {
    return this.api.post<IApiCreateCoverageAccountRes>(`/external-reporting/servers/${serverId}/reports/${reportId}/accounts`, payload);
  }

  updateCoverageAccount(
    serverId: number,
    reportId: number,
    accountId: number,
    payload: IApiCoverageAccountUpdate,
  ): AxiosObservable<void> {
    return this.api.put<void>(`/external-reporting/servers/${serverId}/reports/${reportId}/accounts/${accountId}`, payload);
  }

  deleteCoverageAccount(serverId: number, reportId: number, accountId: number): AxiosObservable<void> {
    return this.api.delete<void>(`/external-reporting/servers/${serverId}/reports/${reportId}/accounts/${accountId}`);
  }

  // ── Coverage rules (per report) ───────────────────────────────
  getCoverageRules(reportId: number): AxiosObservable<IApiGetCoverageRulesRes> {
    return this.api.get<IApiGetCoverageRulesRes>(`/external-reporting/reports/${reportId}/rules`);
  }

  updateCoverageRules(reportId: number, payload: IApiUpdateCoverageRulesReq): AxiosObservable<IApiUpdateCoverageRulesRes> {
    return this.api.put<IApiUpdateCoverageRulesRes>(`/external-reporting/reports/${reportId}/rules`, payload);
  }

  validateCoverageRules(reportId: number, payload: IApiUpdateCoverageRulesReq): AxiosObservable<IApiUpdateCoverageRulesRes> {
    return this.api.post<IApiUpdateCoverageRulesRes>(`/external-reporting/reports/${reportId}/rules/validate`, payload);
  }
}
